import React, { useMemo, useState } from "react";

function formatAuditDate(value) {
  if (!value) return "-";

  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleString();
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  } catch (error) {
    return "-";
  }
}

function prettyEntityLabel(entityType) {
  if (entityType === "rfi") return "RFI";
  if (entityType === "variation") return "Variation Order";
  if (entityType === "claim") return "Claim";
  if (entityType === "certificate") return "Certificate";
  if (entityType === "project") return "Project";
  return entityType || "-";
}

function actionStyle(action) {
  if (action === "delete") {
    return {
      background: "#fef2f2",
      color: "#dc2626",
      fontWeight: 700,
    };
  }

  if (action === "create") {
    return {
      background: "#f0fdf4",
      color: "#16a34a",
      fontWeight: 700,
    };
  }

  if (action === "update" || action === "status_change") {
    return {
      background: "#eff6ff",
      color: "#2563eb",
      fontWeight: 700,
    };
  }

  return {};
}

function payloadPreview(payload) {
  if (!payload || typeof payload !== "object") return "-";

  const entries = Object.entries(payload).filter(([, value]) => value !== "" && value !== null && value !== undefined);
  if (!entries.length) return "-";

  return entries
    .slice(0, 4)
    .map(([key, value]) => `${key}: ${value}`)
    .join(" | ");
}

export default function AuditTrailSection({ auditLogs = [] }) {
  const [entityFilter, setEntityFilter] = useState("All");
  const [actionFilter, setActionFilter] = useState("All");
  const [searchText, setSearchText] = useState("");

  const entityOptions = useMemo(() => {
    const unique = [...new Set(auditLogs.map((item) => item.entityType).filter(Boolean))];
    return ["All", ...unique];
  }, [auditLogs]);

  const actionOptions = useMemo(() => {
    const unique = [...new Set(auditLogs.map((item) => item.action).filter(Boolean))];
    return ["All", ...unique];
  }, [auditLogs]);

  const filteredLogs = useMemo(() => {
    const sorted = [...auditLogs].sort((a, b) => {
      const aTime = typeof a?.createdAt?.toDate === "function"
        ? a.createdAt.toDate().getTime()
        : new Date(a?.createdAt || 0).getTime() || 0;
      const bTime = typeof b?.createdAt?.toDate === "function"
        ? b.createdAt.toDate().getTime()
        : new Date(b?.createdAt || 0).getTime() || 0;
      return bTime - aTime;
    });

    return sorted.filter((item) => {
      const matchesEntity = entityFilter === "All" || item.entityType === entityFilter;
      const matchesAction = actionFilter === "All" || item.action === actionFilter;

      const haystack = [
        item.summary,
        item.entityType,
        item.entityId,
        item.action,
        payloadPreview(item.payload),
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch = !searchText.trim() || haystack.includes(searchText.toLowerCase());

      return matchesEntity && matchesAction && matchesSearch;
    });
  }, [auditLogs, entityFilter, actionFilter, searchText]);

  return (
    <section className="panel">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <h2>Audit Trail</h2>
        <div style={{ fontSize: 14, color: "#6b7280" }}>
          Total Logs: <strong>{auditLogs.length}</strong>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Filters</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 14,
          }}
        >
          <div>
            <label>Entity Type</label>
            <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
              {entityOptions.map((option) => (
                <option key={option} value={option}>
                  {option === "All" ? "All" : prettyEntityLabel(option)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Action</label>
            <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
              {actionOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Search</label>
            <input
              type="text"
              placeholder="Search summary or payload"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Audit Log Register</h3>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date / Time</th>
                <th>User</th>
                <th>Entity</th>
                <th>Record ID</th>
                <th>Action</th>
                <th>Summary</th>
                <th>Payload Preview</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", color: "#6b7280" }}>
                    No audit logs found.
                  </td>
                </tr>
              ) : (
                filteredLogs.map((row) => (
                  <tr key={row.id || `${row.entityId}-${row.createdAt || row.summary}`}>
                    <td>{formatAuditDate(row.createdAt)}</td>
                    <td style={{ fontSize: 12, color: "#374151" }}>{row.userName || row.userEmail || "—"}</td>
                    <td>{prettyEntityLabel(row.entityType)}</td>
                    <td>{row.entityId || "-"}</td>
                    <td style={actionStyle(row.action)}>{row.action || "-"}</td>
                    <td>{row.summary || row.message || "-"}</td>
                    <td>{payloadPreview(row.payload)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}